import { AccessLevel, ResourceType, Scope } from "permissions/resources/permission-resource-types";
import { z } from "zod";

/**
 * The access somebody may ask for (#6596), in the shape a grant of it has, so approving the request
 * grants exactly what was asked for.
 *
 * The API takes one kind for now, General Documents (`RequestableAccess.Parse` in
 * `api/Permissions/Permissions.API/AccessRequests/Services`), because uploading a document is the
 * first refusal that offers a request (#6595).
 */
export type RequestableAccess = Readonly<{
  resourceType: string;
  scope: Scope;
  accessLevel: AccessLevel;
}>;

/**
 * What uploading a document needs: `Document.CreateAsync` asserts General Documents at Need-to-Know
 * and Read/Write (#6595).
 */
export const UPLOAD_DOCUMENTS_ACCESS: RequestableAccess = {
  resourceType: ResourceType.GeneralDocumentAccess,
  scope: Scope["Need-to-Know"],
  accessLevel: AccessLevel["Read/Write"],
};

/** Why an upload control is disabled for a reader without {@link UPLOAD_DOCUMENTS_ACCESS} (#6595). */
export const NO_UPLOAD_ACCESS_TOOLTIP = "You don't have access to upload documents.";

const RequestableAccessZ = z.object({
  resourceType: z.string().min(1),
  scope: z.enum(Scope),
  accessLevel: z.enum(AccessLevel),
});

/**
 * The access a request names, as the API sends it back, or undefined when it cannot be read.
 *
 * Accepts the object, and the same object as a JSON string: the grant endpoints carry a resource as a
 * string (`PermissionCommand.Resource`), and a reply in either form names the same access.
 */
export function readRequestableAccess(value: unknown): RequestableAccess | undefined {
  let candidate = value;
  if (typeof value === "string") {
    try {
      candidate = JSON.parse(value);
    } catch {
      return undefined;
    }
  }
  const parsed = RequestableAccessZ.safeParse(candidate);
  return parsed.success ? parsed.data : undefined;
}

/** Whether two requests name the same access: the type, the scope and the level. */
export function isSameAccess(left: RequestableAccess | undefined, right: RequestableAccess | undefined): boolean {
  if (!left || !right) return false;
  return (
    left.resourceType === right.resourceType && left.scope === right.scope && left.accessLevel === right.accessLevel
  );
}
